import { Notification, useGetNotifications } from '@sit/client-shared';
import { NotificationPayload, NotificationTopic, NotificationTopicSchema, NotificationType } from '@sit/notifications-shared';
import { useIsAuthenticated } from '@web/auth/use-is-authenticated';
import { JSX, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { toastNotification } from './display-notifications';
import { useLatestServerNotification } from './hooks/useLatestServerNotification';
import { NotificationData } from './notification-data';
import { notificationHanderService } from './notification-handler-service';
import { NotificationHandlerContext } from './NotificationHandlerContext';

function shouldAddNotificationToPanel(notification: NotificationPayload): boolean {
  switch (notification.topic) {
    case NotificationTopicSchema.enum.SUBMIT_TIMESHEET:
      return true;
    case NotificationTopicSchema.enum.TIMESHEETS:
      return true;
    default:
      return false;
  }
}

const toMeta = (
  notification: Pick<NotificationPayload, 'data' | 'topic'>,
): {
  meta?: NotificationTopic;
  metaId?: string;
} => {
  const { data } = notification;
  switch (notification.topic) {
    case NotificationTopicSchema.enum.SAVE_TIMESHEET:
    case NotificationTopicSchema.enum.SUBMIT_TIMESHEET:
      return {
        meta: notification.topic,
        metaId: data?.timesheetId ?? data?.metaId,
      };
    case NotificationTopicSchema.enum.TIMESHEETS:
      return {
        meta: notification.topic,
      };
    default:
      return {};
  }
};

const rqToPanelNotification = (notification: Notification): NotificationData => {
  return {
    id: notification.id,
    notification: {
      body: notification.description ?? '',
      title: (notification.message || notification.title) ?? '',
    },
    pushIsRead: notification.pushIsRead,
    timestamp: notification.createdAt,
    type: notification.type satisfies NotificationType,
    ...toMeta({
      topic: notification.meta,
      data: {
        metaId: notification.metaId,
      },
    }),
  };
};

const toPanelNotification = (notification: NotificationPayload): NotificationData => {
  return {
    id: notification.id,
    notification: notification.notification,
    pushIsRead: false,
    timestamp: notification.timestamp,
    type: notification.type,
    ...toMeta(notification),
  };
};

export const NotificationHandlerProvider = ({ children }: PropsWithChildren<Record<string, unknown>>): JSX.Element => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const isAuthenticated = useIsAuthenticated();

  const { data: response } = useGetNotifications(
    {},
    {
      enabled: isAuthenticated,
    },
  );

  useEffect(() => {
    if (response) {
      setNotifications(response.map((n) => rqToPanelNotification(n)));
    }
  }, [response]);

  const addNotification = useCallback((notification: NotificationPayload) => {
    if (shouldAddNotificationToPanel(notification)) {
      setNotifications((n) => [toPanelNotification(notification), ...n]);
    }
  }, []);

  const { data } = useLatestServerNotification();

  useEffect(() => {
    if (data) {
      notificationHanderService.handleNotification(data.newNotification);
      toastNotification(data.newNotification);
      addNotification(data.newNotification);
    }
  }, [data, addNotification]);

  const markAsRead = useCallback(() => {
    setNotifications((n) => n.map((notification) => ({ ...notification, pushIsRead: true })));
  }, []);

  const value = useMemo(() => ({ service: notificationHanderService, notifications, markAsRead }), [notifications, markAsRead]);

  return <NotificationHandlerContext.Provider value={value}>{children}</NotificationHandlerContext.Provider>;
};
